import { useTranslation } from "react-i18next";

import { LANGUAGE_NAMES, LANGUAGES, setLanguage, useLanguage } from "@/lib/i18n/language";
import { cn } from "@/lib/utils";

interface LanguageSwitcherProps {
  /** Placement classes; the switcher is a small white pill, readable on the photo. */
  className?: string;
}

/**
 * NL, EN and FR, for the sign-in page's top right: one button per language,
 * the current one pressed in De Ceuster blauw. Each reads its language's own
 * name to screen readers ("Nederlands", "English", "Français"), in that
 * language. A switch re-renders the page at once and is remembered in this
 * browser.
 */
export function LanguageSwitcher({ className }: LanguageSwitcherProps) {
  const language = useLanguage();
  const { t } = useTranslation();
  return (
    <div
      role="group"
      aria-label={t("language.label")}
      className={cn(
        "bg-card shadow-brand/40 inline-flex items-center gap-1 rounded-full p-1 text-xs font-semibold shadow-[0_12px_28px_-8px]",
        className,
      )}
    >
      {LANGUAGES.map((code) => {
        const current = code === language;
        return (
          <button
            key={code}
            type="button"
            lang={code}
            aria-pressed={current}
            title={LANGUAGE_NAMES[code]}
            onClick={() => setLanguage(code)}
            className={cn(
              "focus-visible:ring-ring rounded-full px-2.5 py-1 leading-none transition-colors focus-visible:ring-2 focus-visible:outline-none pointer-coarse:px-3.5 pointer-coarse:py-4",
              current ? "bg-brand text-brand-foreground" : "text-muted hover:text-foreground",
            )}
          >
            <span aria-hidden="true">{code.toUpperCase()}</span>
            <span className="sr-only">{LANGUAGE_NAMES[code]}</span>
          </button>
        );
      })}
    </div>
  );
}
